import React, { useState, useRef, useEffect, useContext, createContext } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Check, X, Save, Eye, EyeOff, GripVertical, Plus, Trash2, Settings } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Edit Mode Context
export const EditModeContext = createContext({
  editMode: false,
  setEditMode: () => {},
  isAdmin: false,
  pageSlug: '',
  pageContent: null,
  setPageContent: () => {},
  savePageContent: () => {},
  unsavedChanges: false,
});

export const EditModeProvider = ({ children, pageSlug }) => {
  const [editMode, setEditMode] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [pageContent, setPageContent] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [originalContent, setOriginalContent] = useState(null);

  useEffect(() => {
    // Check if user is admin
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setIsAdmin(res.data.role === 'admin');
      }).catch(() => setIsAdmin(false));
    }
  }, []);

  useEffect(() => {
    // Fetch page content
    if (pageSlug) {
      axios.get(`${API}/pages/${pageSlug}`).then(res => {
        setPageContent(res.data);
        setOriginalContent(JSON.stringify(res.data));
      }).catch(err => {
        console.error('Error fetching page:', err);
        setPageContent({ page_slug: pageSlug, title: pageSlug, blocks: [] });
      });
    }
  }, [pageSlug]);

  useEffect(() => {
    if (pageContent && originalContent) {
      setUnsavedChanges(JSON.stringify(pageContent) !== originalContent);
    }
  }, [pageContent, originalContent]);

  const savePageContent = async () => {
    const token = localStorage.getItem('token');
    try {
      await axios.put(`${API}/admin/pages/${pageSlug}`, pageContent, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOriginalContent(JSON.stringify(pageContent));
      setUnsavedChanges(false);
      toast.success('Page saved successfully!');
    } catch (err) {
      toast.error('Failed to save page');
      console.error(err);
    }
  };

  return (
    <EditModeContext.Provider value={{
      editMode,
      setEditMode,
      isAdmin,
      pageSlug,
      pageContent,
      setPageContent,
      savePageContent,
      unsavedChanges,
    }}>
      {children}
    </EditModeContext.Provider>
  );
};

// Admin Edit Toolbar - Fixed position
export const AdminToolbar = () => {
  const { editMode, setEditMode, isAdmin, savePageContent, unsavedChanges } = useContext(EditModeContext);

  if (!isAdmin) return null;

  return (
    <div className="fixed bottom-6 left-6 z-50 flex items-center gap-2" data-testid="admin-toolbar">
      {editMode && unsavedChanges && (
        <Button 
          onClick={savePageContent}
          className="bg-[#78BE20] text-black hover:bg-[#65a31b] shadow-lg"
          data-testid="save-page-btn"
        >
          <Save className="h-4 w-4 mr-2" />
          Save Changes
        </Button>
      )}
      <Button
        onClick={() => setEditMode(!editMode)}
        className={`shadow-lg ${editMode ? 'bg-red-500 hover:bg-red-600' : 'bg-zinc-800 hover:bg-zinc-700'}`}
        data-testid="toggle-edit-btn"
      >
        {editMode ? (
          <>
            <EyeOff className="h-4 w-4 mr-2" />
            Exit Edit Mode
          </>
        ) : (
          <>
            <Pencil className="h-4 w-4 mr-2" />
            Edit Page
          </>
        )}
      </Button>
    </div>
  );
};

// Editable Text Component
export const EditableText = ({ 
  value, 
  onChange, 
  tag = 'p', 
  className = '',
  placeholder = 'Click to edit...',
  multiline = false,
}) => {
  const { editMode } = useContext(EditModeContext);
  const [isEditing, setIsEditing] = useState(false);
  const [tempValue, setTempValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setTempValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (inputRef.current.select) inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    onChange(tempValue);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setTempValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !multiline) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (!editMode) {
    const Tag = tag;
    return <Tag className={className}>{value || placeholder}</Tag>;
  }

  if (isEditing) {
    return (
      <div className="relative inline-block w-full">
        {multiline ? (
          <Textarea
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={`${className} bg-zinc-800 border-[#78BE20] border-2 min-h-[100px]`}
            rows={4}
          />
        ) : (
          <Input
            ref={inputRef}
            value={tempValue}
            onChange={(e) => setTempValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={handleSave}
            className={`${className} bg-zinc-800 border-[#78BE20] border-2`}
          />
        )}
        <div className="absolute -top-8 right-0 flex gap-1">
          <button onClick={handleSave} className="p-1 bg-[#78BE20] rounded text-black">
            <Check className="h-4 w-4" />
          </button>
          <button onClick={handleCancel} className="p-1 bg-zinc-700 rounded text-white">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  const Tag = tag;
  return (
    <Tag 
      className={`${className} cursor-pointer hover:outline hover:outline-2 hover:outline-dashed hover:outline-[#78BE20] relative group`}
      onClick={() => setIsEditing(true)}
    >
      {value || placeholder}
      <span className="absolute -top-6 left-0 text-xs bg-[#78BE20] text-black px-2 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity">
        Click to edit
      </span>
    </Tag>
  );
};

// Editable Image Component
export const EditableImage = ({ 
  src, 
  onChange, 
  alt = '',
  className = '',
}) => {
  const { editMode } = useContext(EditModeContext);
  const [isEditing, setIsEditing] = useState(false);
  const [tempSrc, setTempSrc] = useState(src);

  useEffect(() => {
    setTempSrc(src);
  }, [src]);

  const handleSave = () => {
    onChange(tempSrc);
    setIsEditing(false);
  };

  if (!editMode) {
    return <img src={src} alt={alt} className={className} />;
  }

  if (isEditing) {
    return (
      <div className="relative">
        <img src={tempSrc || src} alt={alt} className={`${className} opacity-50`} />
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/50 p-4">
          <Input
            value={tempSrc}
            onChange={(e) => setTempSrc(e.target.value)}
            placeholder="Enter image URL"
            className="mb-2 bg-zinc-800 border-zinc-600"
          />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} className="bg-[#78BE20] text-black">
              <Check className="h-4 w-4 mr-1" /> Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setIsEditing(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative group cursor-pointer" onClick={() => setIsEditing(true)}>
      <img src={src} alt={alt} className={`${className} group-hover:opacity-70 transition-opacity`} />
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="bg-[#78BE20] text-black px-3 py-1 rounded font-medium">
          <Pencil className="h-4 w-4 inline mr-1" /> Change Image
        </span>
      </div>
    </div>
  );
};

// Editable Block Wrapper - shows edit controls around CMS blocks
export const EditableBlock = ({ 
  children, 
  blockId, 
  blockType,
  onDelete,
  onMoveUp,
  onMoveDown,
  canMoveUp = true,
  canMoveDown = true,
}) => {
  const { editMode } = useContext(EditModeContext);

  if (!editMode) return children;

  return (
    <div className="relative group" data-block-id={blockId}>
      {/* Block Controls */}
      <div className="absolute -left-12 top-1/2 -translate-y-1/2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <button 
          className="p-1.5 bg-zinc-800 border border-zinc-700 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white"
          title="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </div>
      
      {/* Top Controls */}
      <div className="absolute -top-10 left-0 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="text-xs bg-[#78BE20] text-black px-2 py-1 rounded font-medium uppercase">
          {blockType}
        </span>
        {canMoveUp && (
          <button 
            onClick={onMoveUp}
            className="p-1 bg-zinc-800 border border-zinc-700 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white text-xs"
          >
            ↑ Move Up
          </button>
        )}
        {canMoveDown && (
          <button 
            onClick={onMoveDown}
            className="p-1 bg-zinc-800 border border-zinc-700 rounded hover:bg-zinc-700 text-zinc-400 hover:text-white text-xs"
          >
            ↓ Move Down
          </button>
        )}
        <button 
          onClick={onDelete}
          className="p-1 bg-red-500/20 border border-red-500/50 rounded hover:bg-red-500 text-red-400 hover:text-white text-xs"
        >
          <Trash2 className="h-3 w-3 inline mr-1" /> Delete
        </button>
      </div>

      {/* Block Content */}
      <div className="outline-dashed outline-2 outline-transparent group-hover:outline-[#78BE20]/30 rounded-lg transition-all">
        {children}
      </div>
    </div>
  );
};

// Content-aware wrapper that passes CMS data to pages
export const CMSPage = ({ slug, children, renderBlock }) => {
  const { pageContent, setPageContent, editMode, isAdmin } = useContext(EditModeContext);
  
  const updateBlockContent = (blockIndex, newContent) => {
    if (!pageContent) return;
    const newBlocks = [...pageContent.blocks];
    newBlocks[blockIndex] = { ...newBlocks[blockIndex], content: newContent };
    setPageContent({ ...pageContent, blocks: newBlocks });
  };

  const deleteBlock = (blockIndex) => {
    if (!pageContent) return;
    const newBlocks = pageContent.blocks.filter((_, i) => i !== blockIndex);
    setPageContent({ ...pageContent, blocks: newBlocks });
  };

  const moveBlock = (fromIndex, toIndex) => {
    if (!pageContent || toIndex < 0 || toIndex >= pageContent.blocks.length) return;
    const newBlocks = [...pageContent.blocks];
    const [removed] = newBlocks.splice(fromIndex, 1);
    newBlocks.splice(toIndex, 0, removed);
    setPageContent({ ...pageContent, blocks: newBlocks });
  };

  const addBlock = (type, afterIndex = -1) => {
    if (!pageContent) return;
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: {},
      order: afterIndex >= 0 ? afterIndex + 1 : pageContent.blocks.length,
    };
    const newBlocks = [...pageContent.blocks];
    if (afterIndex >= 0) {
      newBlocks.splice(afterIndex + 1, 0, newBlock);
    } else {
      newBlocks.push(newBlock);
    }
    setPageContent({ ...pageContent, blocks: newBlocks });
  };

  // If no CMS blocks, render children as fallback
  if (!pageContent?.blocks?.length) {
    return children;
  }

  return (
    <>
      {pageContent.blocks.map((block, index) => (
        <EditableBlock
          key={block.id || index}
          blockId={block.id}
          blockType={block.type}
          onDelete={() => deleteBlock(index)}
          onMoveUp={() => moveBlock(index, index - 1)}
          onMoveDown={() => moveBlock(index, index + 1)}
          canMoveUp={index > 0}
          canMoveDown={index < pageContent.blocks.length - 1}
        >
          {renderBlock ? renderBlock(block, index, (newContent) => updateBlockContent(index, newContent)) : null}
        </EditableBlock>
      ))}
      
      {/* Add Block Button */}
      {editMode && isAdmin && (
        <div className="py-8 flex justify-center">
          <Button
            variant="outline"
            className="border-dashed border-2 border-zinc-600 hover:border-[#78BE20]"
            onClick={() => addBlock('text')}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Block
          </Button>
        </div>
      )}
    </>
  );
};

export default EditableText;
